/**
 * Carte de profil plongeur.
 *
 * Usage :
 *   import { renderProfileCard } from './profile-card.js';
 *   renderProfileCard(container, { profile });   // pas besoin d'await
 *
 * Le rendu est immédiat (skeleton pendant le fetch Supabase).
 * La section "dernier recensement" se met à jour dès que la réponse arrive.
 *
 * @param {Element} container
 * @param {{ profile?: object|null }} opts
 *   profile : { display_name, username, avatar_url, diver_level, created_at }
 */

import { el, clear, formatDate } from '../js/utils.js';
import { t } from '../js/i18n.js';
import { countSightings, getLatestSighting } from '../js/sightings.js';
import { getSpecies, displayName } from '../js/species.js';

export async function renderProfileCard(container, { profile = null } = {}) {
  const stats = countSightings();
  const latest = getLatestSighting();

  const name = profile?.display_name || profile?.username || t('profile.anonymous');
  const initials = name
    .split(' ')
    .map(w => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const avatar = profile?.avatar_url
    ? el('img', {
        class: 'profile-card__avatar',
        src: profile.avatar_url,
        alt: name,
        loading: 'lazy',
      })
    : el(
        'div',
        { class: 'profile-card__avatar profile-card__avatar--initials', 'aria-hidden': 'true' },
        initials
      );

  const levelBadge = profile?.diver_level
    ? el('span', { class: 'profile-card__level' }, t(`profile.level.${profile.diver_level}`))
    : null;

  const head = el(
    'div',
    { class: 'profile-card__head' },
    avatar,
    el(
      'div',
      { class: 'profile-card__identity' },
      el('p', { class: 'profile-card__name' }, name),
      profile?.username ? el('p', { class: 'profile-card__username' }, `@${profile.username}`) : null,
      levelBadge
    )
  );

  const statsRow = el(
    'div',
    { class: 'profile-card__stats', role: 'list' },
    el('span', { role: 'listitem' }, t('profile.stats.seen', { count: stats.seen })),
    el('span', { class: 'profile-card__stat-sep', 'aria-hidden': 'true' }, '·'),
    el('span', { role: 'listitem' }, t('profile.stats.doubt', { count: stats.doubt }))
  );

  // Slot mis à jour après le fetch — skeleton affiché en attendant
  const sightingBody = el('div', { class: 'profile-card__sighting-body' });

  if (latest) {
    sightingBody.append(
      el('div', {
        class: 'skeleton skeleton--text',
        role: 'status',
        'aria-label': t('common.loading'),
      })
    );
  } else {
    sightingBody.append(
      el('p', { class: 'profile-card__sighting-empty' }, t('profile.no_sighting'))
    );
  }

  const sightingSection = el(
    'div',
    { class: 'profile-card__sighting' },
    el('p', { class: 'profile-card__sighting-label eyebrow' }, t('profile.last_sighting')),
    sightingBody
  );

  clear(container);
  container.append(
    el(
      'article',
      { class: 'profile-card', 'aria-label': name },
      head,
      el('hr', { class: 'profile-card__sep', 'aria-hidden': 'true' }),
      statsRow,
      el('hr', { class: 'profile-card__sep', 'aria-hidden': 'true' }),
      sightingSection
    )
  );

  // Fetch asynchrone — met à jour le slot sans re-rendre la carte entière
  if (latest) {
    try {
      const sp = await getSpecies(latest.speciesId);
      clear(sightingBody);
      sightingBody.append(
        el(
          'div',
          { class: 'profile-card__sighting-row' },
          el('span', { class: 'profile-card__sighting-name' }, displayName(sp)),
          el('span', { class: 'profile-card__sighting-date' }, formatDate(latest.at))
        )
      );
    } catch {
      clear(sightingBody);
      sightingBody.append(
        el('p', { class: 'profile-card__sighting-empty' }, t('profile.no_sighting'))
      );
    }
  }
}
